import { useMemo, useState } from 'react';

import dayjs from 'dayjs';

import { Skeleton, Stack, Typography, tokens } from '@cast/design-system';
import {
  DateRangeFilter,
  RangePicker,
  RangePickerQueryParamKeys,
} from '@cast/shared-date-ui';
import type { BillingV1FeatureUsageDetail } from '@cast/types';

import { useTimeRangeSelection } from 'hooks/useTimeRangeSelection';
import { usePlatformUsageDetailQuery } from 'hooks/queries/organization';

import { useDbInstanceEngines } from './_hooks/useDbInstanceEngines';
import { useResolveThemeColor } from './_hooks/useResolveThemeColor';
import { CommitmentCard, CommitmentData } from './_components/CommitmentCard';
import { DatabaseInstancesTable } from './_components/DatabaseInstancesTable';
import { SummaryCard } from './_components/SummaryCard';
import {
  UsageStackedBarChart,
  buildEntityColorMap,
} from './_components/UsageStackedBarChart';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_SELECTED_INSTANCES = 13;

const DBO_USAGE_RANGE_QUERY_PARAM_KEYS: RangePickerQueryParamKeys = {
  fromDate: 'dbo_usage_from',
  toDate: 'dbo_usage_to',
  dateRangePreset: 'dbo_usage_date_preset',
  timeZone: 'dbo_usage_timezone',
};

const NUMBER_OF_DAYS_IN_13_MONTHS = 397;

const CHART_RANGE_PRESETS = [
  DateRangeFilter.ThisMonth,
  DateRangeFilter.PreviousMonth,
  DateRangeFilter.Last7Days,
  DateRangeFilter.Last30Days,
  DateRangeFilter.Last3Months,
  DateRangeFilter.Last6Months,
  DateRangeFilter.Last9Months,
  DateRangeFilter.Last12Months,
] as const;

// ─── Commitment adapter ───────────────────────────────────────────────────────

/**
 * TODO: Replace with real backend field when available.
 * Currently the platform-usage-detail API does not expose commitment data for
 * database_optimization — returns undefined so CommitmentCard is hidden.
 */
const getDatabaseOptimizationCommitment = (
  _detail: BillingV1FeatureUsageDetail
): CommitmentData | undefined => {
  return undefined;
};

// ─── Component ───────────────────────────────────────────────────────────────

type DatabaseOptimizationUsageDetailProps = {
  feature: string;
};

export const DatabaseOptimizationUsageDetail = ({
  feature,
}: DatabaseOptimizationUsageDetailProps) => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // ── Current-month query for summary card ──────────────────────────────────
  const currentMonthFrom = dayjs().startOf('month').format('YYYY-MM-DD');
  const currentMonthTo = dayjs().endOf('month').format('YYYY-MM-DD');

  const { data: overviewData, isLoading: isOverviewLoading } =
    usePlatformUsageDetailQuery({
      feature,
      periodFrom: currentMonthFrom,
      periodTo: currentMonthTo,
    });
  const overviewDetail = overviewData?.detail;

  // ── Range-driven query for chart + table ──────────────────────────────────
  const { getRange } = useTimeRangeSelection({
    queryParamKeys: DBO_USAGE_RANGE_QUERY_PARAM_KEYS,
    initialState: { preset: DateRangeFilter.ThisMonth },
  });

  const { from: periodFrom, to: periodTo } = useMemo(() => {
    const range = getRange();
    return {
      from: dayjs(range.from).format('YYYY-MM-DD'),
      to: dayjs(range.to).format('YYYY-MM-DD'),
    };
  }, [getRange]);

  const {
    data: chartData,
    isLoading: isChartLoading,
    isError: isChartError,
    refetch: refetchChart,
  } = usePlatformUsageDetailQuery({
    feature,
    periodFrom,
    periodTo,
  });
  const chartDetail = chartData?.detail;

  // ── Engine map (DBO instances list) ───────────────────────────────────────
  const engineMap = useDbInstanceEngines();

  // ── Color map derived from sorted entities ────────────────────────────────
  const resolveColor = useResolveThemeColor();

  const colorMap = useMemo(
    () => buildEntityColorMap(chartDetail?.entities ?? [], resolveColor),
    [chartDetail?.entities, resolveColor]
  );

  // ── Derived values ─────────────────────────────────────────────────────────
  const entities = chartDetail?.entities ?? [];
  const instanceCount = overviewDetail?.entities?.length ?? entities.length;
  const totalUsage = overviewDetail?.totalUsage ?? 0;
  const commitment = overviewDetail
    ? getDatabaseOptimizationCommitment(overviewDetail)
    : undefined;

  const handleRowsSelected = (ids: string[]) => {
    setSelectedRows(ids.slice(0, MAX_SELECTED_INSTANCES));
  };

  // ── Range picker ──────────────────────────────────────────────────────────
  const rangePicker = (
    <RangePicker
      sx={{ width: 250 }}
      filterPresets={CHART_RANGE_PRESETS}
      initialPreset={DateRangeFilter.ThisMonth}
      queryParamKeys={DBO_USAGE_RANGE_QUERY_PARAM_KEYS}
      hideTimezonePicker
      allowTimeInput={false}
      maxNumberOfDays={NUMBER_OF_DAYS_IN_13_MONTHS}
    />
  );

  return (
    <Stack gap={24}>
      {/* ── Current month section ─────────────────────────────────────────── */}
      <Stack gap={16}>
        <Typography color={tokens.theme.fg.structural.primary} variant="h6">
          Current month
        </Typography>

        {isOverviewLoading ? (
          <Skeleton
            variant="rectangular"
            height={100}
            sx={{ borderRadius: '4px' }}
            data-testid="dbo-overview-loading"
          />
        ) : (
          overviewDetail && (
            <Stack direction="row" gap={16} alignItems="stretch">
              <SummaryCard
                totalUsage={totalUsage}
                instanceCount={instanceCount}
              />
              {commitment && <CommitmentCard commitment={commitment} />}
            </Stack>
          )
        )}
      </Stack>

      {/* ── Usage over time chart ─────────────────────────────────────────── */}
      {(chartDetail || isChartLoading) && (
        <UsageStackedBarChart
          entities={entities}
          isLoading={isChartLoading}
          periodFrom={periodFrom}
          periodTo={periodTo}
          selectedEntityIds={selectedRows}
          actions={rangePicker}
        />
      )}

      {/* ── Database instances table ──────────────────────────────────────── */}
      <DatabaseInstancesTable
        entities={entities}
        engineMap={engineMap}
        colorMap={colorMap}
        isLoading={isChartLoading}
        isError={isChartError}
        refetch={refetchChart}
        selectedRows={selectedRows}
        onRowsSelected={handleRowsSelected}
        maxSelection={MAX_SELECTED_INSTANCES}
      />
    </Stack>
  );
};
